import { OrderAsideFormFooter } from '@/components/orders/OrderAside'
import { OrderDetailPanel } from '@/components/orders/OrderDetailPanel'
import { ORDER_ASIDE_FORM_SURFACE_CLASS } from '@/components/orders/order-aside.styles'
import { Typography } from '@/components/ui/typography'
import { useTimezone } from '@/hooks/useTimezoneContext'
import dayjs from '@/lib/dayjs'
import { CustomerPaymentTerms } from '@/lib/enums/CustomerPaymentTerms'
import { serverFieldErrors, submitSchemaValidator, useAppForm } from '@/lib/form'
import type { SalesOrder } from '@/lib/services/sales-orders/sales-orders.types'
import { salesOrderPaymentTermsSchema } from '@/lib/services/sales-orders/sales-order.schema'
import { useUpdateSalesOrderMutation } from '@/lib/services/sales-orders/sales-orders.query'
import { getErrorMessage } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import { CalendarClock } from 'lucide-react'
import { toast } from 'sonner'

const paymentTermsOptions = Object.values(CustomerPaymentTerms).map((term) => ({
  id: term.Value,
  name: term.Name,
}))

type EditOrderPaymentTermsFormProps = {
  order: Pick<SalesOrder, 'id' | 'created_at' | 'payment_terms_code' | 'payment_terms_days'>
  closeAside: () => void
}

export function EditOrderPaymentTermsForm({ order, closeAside }: EditOrderPaymentTermsFormProps) {
  const { orgTimezone } = useTimezone()
  const mutation = useUpdateSalesOrderMutation()
  const form = useAppForm({
    defaultValues: {
      payment_terms_code: order.payment_terms_code || CustomerPaymentTerms.DueOnReceipt.Value,
      payment_terms_days:
        order.payment_terms_code === CustomerPaymentTerms.Custom.Value
          ? String(order.payment_terms_days ?? '')
          : '',
    },
    validators: {
      onSubmit: submitSchemaValidator(salesOrderPaymentTermsSchema),
      onSubmitAsync: async ({ value }) => {
        try {
          await mutation.mutateAsync({
            id: order.id,
            ...salesOrderPaymentTermsSchema.parse(value),
          })
          toast.success('Payment terms updated')
          closeAside()
          return undefined
        } catch (error) {
          toast.error('Payment terms update failed', { description: getErrorMessage(error) })
          return serverFieldErrors(error)
        }
      },
    },
  })
  const values = useStore(form.store, (state) => state.values)
  const terms = salesOrderPaymentTermsSchema.safeParse(values)
  const orderDate = dayjs(order.created_at).tz(orgTimezone)
  const dueDate = terms.success
    ? orderDate.add(terms.data.payment_terms_days, 'day').format('MMM D, YYYY')
    : 'Enter custom days to see the due date.'

  return (
    <form.AppForm>
      <form.Form className="flex min-h-full flex-col pt-5 pl-0 sm:pt-6 sm:pl-1">
        <OrderDetailPanel
          title="Payment Terms"
          subtitle="Update when payment is due for this order."
          icon={CalendarClock}
          tone="warning"
          className="shadow-none"
        >
          <div className={ORDER_ASIDE_FORM_SURFACE_CLASS}>
            <div className="grid gap-y-5">
              <form.AppField name="payment_terms_code">
                {(field) => (
                  <field.SelectInput label="Payment Terms" options={paymentTermsOptions} />
                )}
              </form.AppField>
              {values.payment_terms_code === CustomerPaymentTerms.Custom.Value ? (
                <form.AppField name="payment_terms_days">
                  {(field) => (
                    <field.TextInput label="Custom Days" placeholder="30" inputMode="numeric" />
                  )}
                </form.AppField>
              ) : null}
              <div className="space-y-2" aria-live="polite">
                <Typography variant="eyebrow">Due date</Typography>
                <Typography variant="cardTitle" className="block">
                  {dueDate}
                </Typography>
                <Typography variant="supporting" className="block">
                  Calculated from the order date, {orderDate.format('MMM D, YYYY')}.
                </Typography>
              </div>
              <Typography variant="supporting" className="block">
                This changes only this order. The customer’s default payment terms stay the same.
              </Typography>
            </div>
          </div>
        </OrderDetailPanel>
        <OrderAsideFormFooter
          form={form}
          mutation={mutation}
          saveLabel="Save Payment Terms"
          onCancel={closeAside}
        />
      </form.Form>
    </form.AppForm>
  )
}
